/**
 * PaymentsMobile — мобильная раскладка «Платежей» (design Handoff 4): тёмная
 * сводка, поиск + фильтр, список транзакций по дням. Тап по строке — детали
 * (модалка оркестратора), фильтры/период — filter-sheet. Справа в шапке
 * резерв под гамбургер AppShell (pr-[60px]).
 */

import {useState} from 'react';
import {Download, ListFilter} from 'lucide-react';

import type {PaymentRow as Row, PaymentsBundle, PaymentStatus} from '@services/payments';
import type {Range} from '@components/PeriodPicker';
import {SearchField} from '@components/SearchField';
import {Loader} from '@components/ui/Loader';

import {groupByDay} from '../Payments.utils';

import {PaymentRow} from './PaymentRow';
import {PaymentsFilterSheet} from './PaymentsFilterSheet';
import {PaymentsSummaryCard} from './PaymentsSummaryCard';

type PaymentsMobileProps = {
  data?: PaymentsBundle;
  isLoading: boolean;
  rows: Row[];
  search: string;
  onSearch: (v: string) => void;
  status: PaymentStatus | null;
  onStatus: (v: PaymentStatus | null) => void;
  period: Range;
  periodLabel: string;
  onPeriod: (p: Range) => void;
  onDetail: (row: Row) => void;
  onSoon: (text: string) => void;
};

export const PaymentsMobile = ({
  data,
  isLoading,
  rows,
  search,
  onSearch,
  status,
  onStatus,
  period,
  periodLabel,
  onPeriod,
  onDetail,
  onSoon,
}: PaymentsMobileProps) => {
  const [filterOpen, setFilterOpen] = useState(false);
  const groups = groupByDay(rows);

  return (
    <div className="flex min-h-full flex-col">
      <header className="flex items-start justify-between pb-3 pl-[18px] pr-[60px] pt-3">
        <div>
          <h1 className="text-[23px] font-bold -tracking-[.02em]">Платежи</h1>
          <div className="mt-0.5 text-xs text-muted-foreground">{periodLabel} · для сверки</div>
        </div>
        <button
          onClick={() => onSoon('Экспорт — скоро')}
          aria-label="Экспорт"
          className="grid size-10 shrink-0 self-start place-items-center rounded-xl border border-input bg-card text-muted-foreground"
        >
          <Download className="size-[18px]" strokeWidth={1.8} />
        </button>
      </header>

      <div className="flex flex-col gap-3.5 px-4 pb-6 pt-0.5">
        {isLoading || !data ? (
          <div className="flex min-h-[380px] items-center justify-center">
            <Loader label="Загружаем платежи…" />
          </div>
        ) : (
          <>
            <PaymentsSummaryCard summary={data.summary} />

            <div className="flex gap-2.5">
              <SearchField
                value={search}
                onChange={onSearch}
                placeholder="Поиск по клиенту…"
                className="h-[46px] min-w-0 flex-1 rounded-xl border border-input bg-card px-[13px]"
              />
              <button
                onClick={() => setFilterOpen(true)}
                aria-label="Фильтры"
                className="relative grid size-[46px] shrink-0 place-items-center rounded-xl border border-input bg-card"
              >
                <ListFilter className="size-5 text-skr-ink" strokeWidth={1.8} />
                {status !== null && (
                  <span className="absolute -right-1 -top-1 size-2.5 rounded-full bg-primary" />
                )}
              </button>
            </div>

            {groups.length === 0 && (
              <div className="py-12 text-center text-[13px] text-muted-foreground">
                Платежей за период нет
              </div>
            )}
            {groups.map((g) => (
              <div key={g.date} className="flex flex-col gap-2.5">
                <div className="px-0.5 pt-0.5 font-mono text-xs font-semibold text-skr-label">
                  {g.label}
                </div>
                {g.rows.map((r) => (
                  <PaymentRow key={r.id} row={r} onOpen={onDetail} />
                ))}
              </div>
            ))}
          </>
        )}
      </div>

      <PaymentsFilterSheet
        open={filterOpen}
        onOpenChange={setFilterOpen}
        status={status}
        onStatus={onStatus}
        period={period}
        onPeriod={onPeriod}
        shownCount={data?.totalCount ?? 0}
      />
    </div>
  );
};
